import { html, TemplateResult } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_EXCHANGE_ANTIMALWARE_SCAN_TASKS_FORM } from '../../../../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../../../../utils/translator.helper';
import { styles } from '../view/styles';
import { GzFormFieldWebComponent } from '../../../../../../../../../../ui/gzForm/formField';
import { GzInputWebComponent } from '../../../../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { RequiredValidator } from '../../../../../../../../../../ui/gzForm/validators/required.validator';
import { FormGroup } from '../../../../../../../../../../ui/gzForm/formGroup/form-group';
import { GzInputNumberWebComponent, InputWidthValues } from '../../../../../../../../../../ui/gzInput';
import { GzCheckboxWebComponent } from '../../../../../../../../../../ui/gzCheckbox';
import { GzPolicyFullPageWindowFormWebComponent } from '../../../../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/form';
import { GzDropdownWebComponent } from '../../../../../../../../../../ui/gzDropdown';
import { toggleDisabled, toggleVisibility } from '../../../../../../../../../../utils/htmlElement.utils';
import { GzChipsGeneratorInputWebComponent } from '../../../../../../../../../../ui/gzChipsGeneratorInput';
import {
  DEFAULT_DEPTH,
  DEFAULT_RECURRENCE_STEP,
  DEFAULT_SCAN_ONLY_RECURRENCE_STEP,
  DEFAULT_SIZE,
  getExchangeAntimalwareActionRuleFormOptions,
  getExchangeAntimalwareAlternativeActionRuleFormOptions,
  getExchangeAntimalwareScanModeRuleFormOptions,
  getExchangeAntimalwareScanTaskTargetTypeFormOptions,
  MAX_RECURRENCE_STEP,
  MAX_SIZE,
  MIN_RECURRENCE_STEP,
  MIN_SIZE,
} from '../../../../../../../utils/uiSettings/exchange/exchange-protection.values';
import {
  ExchangeAntimalwareRuleActionType,
  ExchangeAntimalwareRuleSettingsScanMode,
  ExchangeAntimalwareScanTask,
  ExchangeAntimalwareScanTaskTargetExclusion,
  ExchangeAntimalwareScanTaskTargetType,
  ExchangeAntimalwareScanTaskType,
  RecurrenceType,
  RepeatInterval,
} from '@nirvana/shared/dtos/types/policies/common';
import { GzRadioGroupWebComponent } from '../../../../../../../../../../ui/gzRadio';
import { GzHardCodedFormField } from 'apps/webcomponents/src/ui/gzForm/formField/component/GzHardCodedFormField';
import { FontSizes, FontWeights, LabelWidthValues } from '../../../../../../../../../../types/common/types';
import {
  convertScanTaskStartDateDbValueToUiValue,
  convertScanTaskStartDateUiValueToDbValue,
  getCurrentScanTaskStartDate,
} from '../utils/converters';
import { GzDatePickerWebComponent } from '../../../../../../../../../../ui/gzDatePicker';
import { GzTimePickerWebComponent } from '../../../../../../../../../../ui/gzTimePicker';
import {
  getRepeatIntervalFormOptions,
  getWeekDaysFormOptions,
  WeekDaysValues,
  WeekDayValueToScheduleFieldNameExchange,
} from '../../../../../../../../../common/policyPanel/uiSettings/schedulerTemplate/types';
import { GzMultiSelectGroupWebComponent } from '../../../../../../../../../../ui/gzMultiSelect';
import { GzExchangeAntimalwareScanTaskExclusionsGridWebComponent } from '../gridComponents/GzExchangeAntimalwareScanTaskExclusionsGridWebComponent';
import _ from 'lodash';
import { generateRandomTaskId } from '../../../../../../../../../common/policyPanel/uiSettings/taskIdTemplate';
import { CustomizableValidator } from '../../../../../../../../../../ui/gzForm/validators/customizable.validator';
import { validateTargetScanObjectGroup } from '../utils/validators';
import { EXTENSION_VALIDATION_REGEX } from '../../../../../helpers/utils/validators';

@customElement(WEB_COMPONENT_POLICY_PANEL_EXCHANGE_ANTIMALWARE_SCAN_TASKS_FORM)
export class GzExchangeAntimalwareScanTasksFormWebComponent extends GzPolicyFullPageWindowFormWebComponent<ExchangeAntimalwareScanTask> {
  // general section
  @query('#name')
  ruleName: GzFormFieldWebComponent<GzInputWebComponent, string>;

  // scheduler section
  @query('#tasks-scheduler-start-date-field')
  private schedulerStartDateField: GzFormFieldWebComponent<GzDatePickerWebComponent, any>;
  @query('#tasks-scheduler-start-time-field')
  private schedulerStartTimeField: GzFormFieldWebComponent<GzTimePickerWebComponent, any>;
  @query('#tasks-scheduler-recurrence-type-field')
  private recurrenceTypeFormField: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;
  @query('#tasks-scheduler-recurrence-step-field')
  private recurrenceStepFormField: GzFormFieldWebComponent<GzInputNumberWebComponent>;
  @query('#tasks-scheduler-recurrence-step-type-field')
  private recurrenceStepTypeField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#tasks-scheduler-recurrence-run-every-field')
  private recurrenceRunEveryField: GzFormFieldWebComponent<GzMultiSelectGroupWebComponent>;
  @query('#tasks-scheduler-stop-if-longer-enabled-field')
  private stopIfLongerEnabledField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#tasks-scheduler-stop-if-longer-step-field')
  private stopIfLongerStepField: GzFormFieldWebComponent<GzInputNumberWebComponent>;
  @query('#tasks-scheduler-stop-if-longer-step-type-field')
  private stopIfLongerStepTypeField: GzFormFieldWebComponent<GzDropdownWebComponent>;

  // target section
  @query('#tasks-target-type-field')
  private targetTypeField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#tasks-target-scan-emails-field')
  private targetScanEmailsField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#tasks-target-scan-contacts-field')
  private targetScanContactsField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#tasks-target-scan-tasks-field')
  private targetScanTasksField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#tasks-target-scan-appointments-field')
  private targetScanAppointmentsField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#tasks-target-scan-post-items-field')
  private targetScanPostItemsField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#tasks-target-scan-only-unread-emails-field')
  private targetScanOnlyUnreadEmailsField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#tasks-target-scan-only-items-with-attachments-field')
  private targetScanOnlyItemsWithAttachmentsField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#tasks-target-scan-only-recent-received-enabled-field')
  private targetScanOnlyRecentReceivedEnabledField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#tasks-target-scan-only-recent-received-step-field')
  private targetScanOnlyRecentReceivedStepField: GzFormFieldWebComponent<GzInputNumberWebComponent>;
  @query('#tasks-target-scan-only-recent-received-step-type-field')
  private targetScanOnlyRecentReceivedStepTypeField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#tasks-target-exclusions-enabled-field')
  private targetExclusionsEnabledField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#tasks-target-exclusions-field')
  private targetExclusionsField: GzFormFieldWebComponent<
    GzExchangeAntimalwareScanTaskExclusionsGridWebComponent,
    ExchangeAntimalwareScanTaskTargetExclusion[]
  >;

  // options section
  @query('#rule-settings-scan-mode-field')
  private ruleSettingsScanModeField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#rule-settings-extensions-field')
  private ruleSettingsExtensionsField: GzFormFieldWebComponent<GzChipsGeneratorInputWebComponent>;
  @query('#rule-settings-maximum-size-enabled-field')
  private ruleSettingsMaximumSizeEnabledField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#rule-settings-maximum-size-field')
  private ruleSettingsMaximumSizeField: GzFormFieldWebComponent<GzInputNumberWebComponent, number>;
  @query('#rule-settings-maximum-depth-enabled-field')
  private ruleSettingsMaximumDepthEnabledField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#rule-settings-maximum-depth-field')
  private ruleSettingsMaximumDepthField: GzFormFieldWebComponent<GzInputNumberWebComponent, number>;
  @query('#rule-settings-scan-pua-field')
  private ruleSettingsScanPuaField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  // action section
  @query('#rule-actions-infected-action-field')
  private ruleActionsInfectedActionField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#rule-actions-infected-then-field')
  private ruleActionsInfectedThenField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#rule-actions-suspected-action-field')
  private ruleActionsSuspectedActionField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#rule-actions-suspected-then-field')
  private ruleActionsSuspectedThenField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#rule-actions-unscannable-action-field')
  private ruleActionsUnscannableActionField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#rule-actions-unscannable-then-field')
  private ruleActionsUnscannableThenField: GzFormFieldWebComponent<GzDropdownWebComponent>;

  public constructor(value?: ExchangeAntimalwareScanTask) {
    super(value);
  }

  static get styles() {
    return styles;
  }

  public setError(error: string) {
    this.ruleName.setError(error);
  }

  protected getConvertedFormValue() {
    const formValue = _.cloneDeep(this.formGroup.value);

    // set task id if missing
    const taskId = _.get(formValue, ['taskId'], '');
    if (taskId === '') {
      _.set(formValue, ['taskId'], generateRandomTaskId());
    }

    // convert scheduler
    const startDateUiValue = _.get(formValue, ['scheduler', 'startDate'], {});
    const startDateDbValue = convertScanTaskStartDateUiValueToDbValue(startDateUiValue);
    _.set(formValue, ['scheduler', 'startDate'], startDateDbValue);

    // compute week days fields from form value
    const weekdays = _.get(formValue, ['scheduler', 'recurrenceRunEvery'], []);
    WeekDaysValues.forEach(day =>
      _.set(formValue, ['scheduler', WeekDayValueToScheduleFieldNameExchange[day]], weekdays.includes(day) ? 1 : 0)
    );
    _.unset(formValue, ['scheduler', 'recurrenceRunEvery']);

    return formValue;
  }

  createFormGroup(): void {
    this.ruleName.Validators = [RequiredValidator(translateFn('Required field'))];
    this.recurrenceStepFormField.Validators = [RequiredValidator(translateFn('Required field'))];
    this.recurrenceStepTypeField.Validators = [RequiredValidator(translateFn('Required field'))];
    this.ruleSettingsExtensionsField.Validators = [RequiredValidator(translateFn('Required field'))];

    // set validators for target scan object group
    const targetScanObjectGroupValidator = [
      CustomizableValidator(() =>
        validateTargetScanObjectGroup([
          this.targetScanEmailsField.value as boolean,
          this.targetScanContactsField.value as boolean,
          this.targetScanTasksField.value as boolean,
          this.targetScanAppointmentsField.value as boolean,
          this.targetScanPostItemsField.value as boolean,
        ])
      ),
    ];
    this.targetScanEmailsField.Validators = targetScanObjectGroupValidator;
    this.targetScanContactsField.Validators = targetScanObjectGroupValidator;
    this.targetScanTasksField.Validators = targetScanObjectGroupValidator;
    this.targetScanAppointmentsField.Validators = targetScanObjectGroupValidator;
    this.targetScanPostItemsField.Validators = targetScanObjectGroupValidator;

    this.recurrenceRunEveryField.touched = true;
    this.targetExclusionsField.touched = true;

    this.formGroup = FormGroup.build({
      taskId: new GzHardCodedFormField<string>(''),
      name: this.ruleName,
      scanType: new GzHardCodedFormField<number>(ExchangeAntimalwareScanTaskType.CUSTOM),
      scheduler: {
        startDate: {
          date: this.schedulerStartDateField,
          time: this.schedulerStartTimeField,
        },
        recurrenceType: this.recurrenceTypeFormField,
        recurrenceStep: this.recurrenceStepFormField,
        recurrenceStepType: this.recurrenceStepTypeField,
        recurrenceRunEvery: this.recurrenceRunEveryField,
        stopIfLongerEnabled: this.stopIfLongerEnabledField,
        stopIfLongerStep: this.stopIfLongerStepField,
        stopIfLongerStepType: this.stopIfLongerStepTypeField,
      },
      target: {
        targetType: this.targetTypeField,
        scanEmails: this.targetScanEmailsField,
        scanContacts: this.targetScanContactsField,
        scanTasks: this.targetScanTasksField,
        scanAppointments: this.targetScanAppointmentsField,
        scanPostItems: this.targetScanPostItemsField,
        scanOnlyUnreadEmails: this.targetScanOnlyUnreadEmailsField,
        scanOnlyItemsWithAttachments: this.targetScanOnlyItemsWithAttachmentsField,
        scanOnlyRecentReceivedEnabled: this.targetScanOnlyRecentReceivedEnabledField,
        scanOnlyRecentReceivedStep: this.targetScanOnlyRecentReceivedStepField,
        scanOnlyRecentReceivedStepType: this.targetScanOnlyRecentReceivedStepTypeField,
        exclusionsEnabled: this.targetExclusionsEnabledField,
        exclusions: this.targetExclusionsField,
      },
      options: {
        scanMode: this.ruleSettingsScanModeField,
        extensions: this.ruleSettingsExtensionsField,
        maximumSizeEnabled: this.ruleSettingsMaximumSizeEnabledField,
        maximumSize: this.ruleSettingsMaximumSizeField,
        maximumDepthEnabled: this.ruleSettingsMaximumDepthEnabledField,
        maximumDepth: this.ruleSettingsMaximumDepthField,
        scanPUA: this.ruleSettingsScanPuaField,
      },
      actions: {
        infected: {
          action: this.ruleActionsInfectedActionField,
          then: this.ruleActionsInfectedThenField,
        },
        suspected: {
          action: this.ruleActionsSuspectedActionField,
          then: this.ruleActionsSuspectedThenField,
        },
        unscannable: {
          action: this.ruleActionsUnscannableActionField,
          then: this.ruleActionsUnscannableThenField,
        },
      },
    });
  }

  setFormGroupValue() {
    if (!this.value) {
      // set current date and time
      const currentStartDate = getCurrentScanTaskStartDate();
      this.schedulerStartDateField.value = currentStartDate.date;
      this.schedulerStartTimeField.value = currentStartDate.time;
      return;
    }

    const startDate = convertScanTaskStartDateDbValueToUiValue(this.value.scheduler.startDate);
    const weekDays = WeekDaysValues.filter(x => this.value?.scheduler[WeekDayValueToScheduleFieldNameExchange[x]]);

    this.formGroup.value = {
      taskId: this.value.taskId,
      name: this.value.name,
      scanType: this.value.scanType,
      scheduler: {
        startDate: startDate,
        recurrenceType: this.value.scheduler.recurrenceType,
        recurrenceStep: this.value.scheduler.recurrenceStep,
        recurrenceStepType: this.value.scheduler.recurrenceStepType,
        recurrenceRunEvery: weekDays,
        stopIfLongerEnabled: this.value.scheduler.stopIfLongerEnabled,
        stopIfLongerStep: this.value.scheduler.stopIfLongerStep,
        stopIfLongerStepType: this.value.scheduler.stopIfLongerStepType,
      },
      target: this.value.target,
      options: this.value.options,
      actions: this.value.actions,
    };
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    const repeatIntervalOptionsForRecurrence = [RepeatInterval.HOURS, RepeatInterval.DAYS, RepeatInterval.WEEKDAYS];
    const repeatIntervalFormOptionsForRecurrence = repeatIntervalOptionsForRecurrence.map(
      (repeatInterval: RepeatInterval) => getRepeatIntervalFormOptions()[repeatInterval]
    );
    const repeatIntervalOptionsForStop = [RepeatInterval.HOURS, RepeatInterval.DAYS];
    const repeatIntervalFormOptionsForStop = repeatIntervalOptionsForStop.map(
      (repeatInterval: RepeatInterval) => getRepeatIntervalFormOptions()[repeatInterval]
    );
    const weekDaysFormOptions = getWeekDaysFormOptions();

    return html`
      <div class="tasks-form-container">
        <!-- General section -->
        <div id="tasks-general-section">
          <gz-title fontWeight="500" fontSize="18" class="module-subtitle">${translateFn('General')}</gz-title>
          <gz-form-field id="name" class="label-mw-250" label="${translateFn('Task name')}" required>
            <gz-input
              inputWidth="${InputWidthValues.LARGE}"
              gz-form-input
              placeholder="${translateFn('Enter the task name')}"
            ></gz-input>
            <gz-input-error></gz-input-error>
          </gz-form-field>
        </div>

        <!-- Scheduler section -->
        <div id="tasks-scheduler-section">
          <gz-title fontWeight="500" fontSize="18" class="module-subtitle mt-5">${translateFn('Scheduler')}</gz-title>

          <div id="tasks-scheduler-date-and-time-section">
            <gz-form-field
              id="tasks-scheduler-start-date-field"
              class="label-mw-250 mr-2"
              label="${translateFn('Start date and time')}"
            >
              <gz-date-picker
                gz-form-input
                placeholder="${translateFn('day/month/year')}"
                labelWidth="${LabelWidthValues.MEDIUM}"
                navigateToSelected="True"
              ></gz-date-picker>
              <gz-input-error></gz-input-error>
            </gz-form-field>

            <gz-form-field id="tasks-scheduler-start-time-field" class="label-mw-250">
              <gz-time-picker gz-form-input></gz-time-picker>
              <gz-input-error></gz-input-error>
            </gz-form-field>
          </div>

          <div id="tasks-scheduler-recurrence-section">
            <gz-text class="form-field-label mb-2">${translateFn('Recurrence') + ':'}</gz-text>

            <gz-form-field class="pl-5" id="tasks-scheduler-recurrence-type-field">
              <gz-radio-group
                gz-form-input
                class="d-flex flex-column"
                .selectedOption=${RecurrenceType.SCHEDULE}
                @radioSelectionChanged=${this.onSchedulerRecurrenceTypeChanged}
              >
                <div class="radio" id="policy-panel-recurrence-schedule">
                  <gz-radio
                    id="policy-panel-recurrence-schedule-radio"
                    label="${translateFn('Schedule task to run once every') + ':'}"
                    .value=${RecurrenceType.SCHEDULE}
                    wrapped
                  ></gz-radio>

                  <gz-form-field id="tasks-scheduler-recurrence-step-field" class="mr-2">
                    <gz-input-number
                      gz-form-input
                      .min=${MIN_RECURRENCE_STEP}
                      .max=${MAX_RECURRENCE_STEP}
                      .value=${DEFAULT_RECURRENCE_STEP}
                    ></gz-input-number>
                    <gz-input-error></gz-input-error>
                  </gz-form-field>
                  <gz-form-field id="tasks-scheduler-recurrence-step-type-field">
                    <gz-dropdown
                      gz-form-input
                      .options=${repeatIntervalFormOptionsForRecurrence}
                      .value=${RepeatInterval.WEEKDAYS}
                    ></gz-dropdown>
                    <gz-input-error></gz-input-error>
                  </gz-form-field>
                </div>

                <div class="radio" id="policy-panel-recurrence-weekdays">
                  <gz-radio
                    id="policy-panel-recurrence-weekdays-radio"
                    label="${translateFn('Run task every') + ':'}"
                    .value=${RecurrenceType.WEEKDAYS}
                  ></gz-radio>

                  <gz-form-field id="tasks-scheduler-recurrence-run-every-field" class="disabled">
                    <gz-multiselect-group gz-form-input .options=${weekDaysFormOptions}></gz-multiselect-group>
                    <gz-input-error></gz-input-error>
                  </gz-form-field>
                </div>
              </gz-radio-group>
            </gz-form-field>

            <div class="checkbox-with-input">
              <gz-form-field class="checkbox-width-250" id="tasks-scheduler-stop-if-longer-enabled-field">
                <gz-checkbox
                  label="${translateFn('Stop scan if it takes longer than') + ':'}"
                  gz-form-input
                  @change="${e => {
                    toggleDisabled(this.stopIfLongerStepField, !e.detail.checked);
                    toggleDisabled(this.stopIfLongerStepTypeField, !e.detail.checked);
                  }}"
                ></gz-checkbox>
              </gz-form-field>

              <gz-form-field id="tasks-scheduler-stop-if-longer-step-field" class="mr-2 disabled">
                <gz-input-number
                  gz-form-input
                  .min=${MIN_RECURRENCE_STEP}
                  .max=${MAX_RECURRENCE_STEP}
                  .value=${DEFAULT_RECURRENCE_STEP}
                ></gz-input-number>
              </gz-form-field>
              <gz-form-field id="tasks-scheduler-stop-if-longer-step-type-field" class="disabled">
                <gz-dropdown
                  gz-form-input
                  .options=${repeatIntervalFormOptionsForStop}
                  .value=${RepeatInterval.DAYS}
                ></gz-dropdown>
                <gz-input-error></gz-input-error>
              </gz-form-field>
            </div>
          </div>
        </div>

        <!-- Target section -->
        <div id="tasks-target-section">
          <gz-title fontWeight="500" fontSize="18" class="module-subtitle mt-5">${translateFn('Target')}</gz-title>

          <gz-form-field id="tasks-target-type-field" label="${translateFn('Scan')}">
            <gz-dropdown
              gz-form-input
              .options=${getExchangeAntimalwareScanTaskTargetTypeFormOptions()}
              .value=${ExchangeAntimalwareScanTaskTargetType.MAILBOXES_AND_PUBLIC_FOLDERS}
            ></gz-dropdown>
          </gz-form-field>

          <div class="container">
            <div class="objects">
              <gz-title fontWeight="${FontWeights.SEMIBOLD}" fontSize="${FontSizes.PARAGRAPH_12}" class="mt-2 subtitle"
                >${translateFn('Objects')}</gz-title
              >
              <gz-form-field id="tasks-target-scan-emails-field">
                <gz-checkbox
                  label="${translateFn('Emails')}"
                  gz-form-input
                  checked
                  @change="${this.onTargetScanObjectChanged}"
                ></gz-checkbox>
              </gz-form-field>
              <gz-form-field id="tasks-target-scan-contacts-field">
                <gz-checkbox
                  label="${translateFn('Contacts')}"
                  gz-form-input
                  checked
                  @change="${this.onTargetScanObjectChanged}"
                ></gz-checkbox>
              </gz-form-field>
              <gz-form-field id="tasks-target-scan-tasks-field">
                <gz-checkbox
                  label="${translateFn('Tasks')}"
                  gz-form-input
                  checked
                  @change="${this.onTargetScanObjectChanged}"
                ></gz-checkbox>
              </gz-form-field>
              <gz-form-field id="tasks-target-scan-appointments-field">
                <gz-checkbox
                  label="${translateFn('Appointments')}"
                  gz-form-input
                  checked
                  @change="${this.onTargetScanObjectChanged}"
                ></gz-checkbox>
              </gz-form-field>
              <gz-form-field id="tasks-target-scan-post-items-field">
                <gz-checkbox
                  label="${translateFn('Post Items')}"
                  gz-form-input
                  checked
                  @change="${this.onTargetScanObjectChanged}"
                ></gz-checkbox>
                <gz-input-error></gz-input-error>
              </gz-form-field>
            </div>

            <div class="restrictions">
              <gz-title fontWeight="${FontWeights.SEMIBOLD}" fontSize="${FontSizes.PARAGRAPH_12}" class="mt-2 subtitle"
                >${translateFn('Restrictions')}</gz-title
              >
              <gz-form-field id="tasks-target-scan-only-unread-emails-field">
                <gz-checkbox label="${translateFn('Scan only unread emails')}" gz-form-input></gz-checkbox>
              </gz-form-field>
              <gz-form-field id="tasks-target-scan-only-items-with-attachments-field">
                <gz-checkbox
                  label="${translateFn('Scan only items with attachments')}"
                  gz-form-input
                  checked
                ></gz-checkbox>
              </gz-form-field>
              <div class="checkbox-with-input">
                <gz-form-field class="checkbox-width-290" id="tasks-target-scan-only-recent-received-enabled-field">
                  <gz-checkbox
                    label="${translateFn('Scan only items received in the last') + ':'}"
                    gz-form-input
                    @change="${e => {
                      toggleDisabled(this.targetScanOnlyRecentReceivedStepField, !e.detail.checked);
                      toggleDisabled(this.targetScanOnlyRecentReceivedStepTypeField, !e.detail.checked);
                    }}"
                    checked
                  ></gz-checkbox>
                </gz-form-field>

                <gz-form-field id="tasks-target-scan-only-recent-received-step-field" class="mr-2">
                  <gz-input-number
                    gz-form-input
                    .min=${MIN_RECURRENCE_STEP}
                    .max=${MAX_RECURRENCE_STEP}
                    .value=${DEFAULT_SCAN_ONLY_RECURRENCE_STEP}
                  ></gz-input-number>
                </gz-form-field>
                <gz-form-field id="tasks-target-scan-only-recent-received-step-type-field">
                  <gz-dropdown
                    gz-form-input
                    .options=${repeatIntervalFormOptionsForStop}
                    .value=${RepeatInterval.HOURS}
                  ></gz-dropdown>
                  <gz-input-error></gz-input-error>
                </gz-form-field>
              </div>
            </div>
          </div>

          <div id="tasks-target-exclusions" class="mt-2">
            <gz-form-field id="tasks-target-exclusions-enabled-field">
              <gz-toggle gz-form-input @toggleChange="${this.onExclusionsEnabledChanged}">
                <gz-title fontWeight="${FontWeights.SEMIBOLD}" fontSize="${FontSizes.PARAGRAPH_12}" class="subtitle"
                  >${translateFn('Exclusions')}</gz-title
                >
              </gz-toggle>
            </gz-form-field>

            <gz-form-field id="tasks-target-exclusions-field" class="hidden">
              <gz-policy-panel-exchange-antimalware-scan-tasks-exclusions-grid
                gz-form-input
              ></gz-policy-panel-exchange-antimalware-scan-tasks-exclusions-grid>
              <gz-input-error></gz-input-error>
            </gz-form-field>
          </div>
        </div>

        <!-- Rule options section -->
        <div id="rules-settings-section">
          <gz-title id="rules-settings-title" fontWeight="500" fontSize="18" class="module-subtitle mt-2"
            >${translateFn('Options')}</gz-title
          >
          <gz-form-field id="rule-settings-scan-mode-field" label="${translateFn('Scanned file types')}">
            <gz-dropdown
              gz-form-input
              .options=${getExchangeAntimalwareScanModeRuleFormOptions()}
              .value=${ExchangeAntimalwareRuleSettingsScanMode.ALL}
              @selectionChanged="${this.handleSettingsScanModeFieldChange}"
            ></gz-dropdown>
          </gz-form-field>

          <gz-form-field class="label-mw-250 hidden" id="rule-settings-extensions-field" disabled>
            <gz-chips-generator-input
              gz-form-input
              themeInput
              validationRegex="${EXTENSION_VALIDATION_REGEX}"
              .strings=${{
                placeholder: translateFn('Specify extensions here'),
                notUniqueErrorMessage: translateFn('The entry already exists.'),
                invalidItemErrorMessage: translateFn('Invalid extension'),
              }}
            >
            </gz-chips-generator-input>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <div class="checkbox-with-input">
            <gz-form-field class="checkbox-width-410" id="rule-settings-maximum-size-enabled-field">
              <gz-checkbox
                label="${translateFn('Attachment/email body maximum size (MB)') + ':'}"
                gz-form-input
                @change="${e => toggleDisabled(this.ruleSettingsMaximumSizeField, !e.detail.checked)}"
              ></gz-checkbox>
            </gz-form-field>

            <gz-form-field id="rule-settings-maximum-size-field" class="disabled">
              <gz-input-number gz-form-input .min=${MIN_SIZE} .max=${MAX_SIZE} .value=${DEFAULT_SIZE}></gz-input-number>
            </gz-form-field>
          </div>

          <div class="checkbox-with-input">
            <gz-form-field class="checkbox-width-410" id="rule-settings-maximum-depth-enabled-field">
              <gz-checkbox
                label="${translateFn('Archive maximum depth (levels)') + ':'}"
                gz-form-input
                @change="${e => toggleDisabled(this.ruleSettingsMaximumDepthField, !e.detail.checked)}"
                checked
              ></gz-checkbox>
            </gz-form-field>

            <gz-form-field id="rule-settings-maximum-depth-field">
              <gz-input-number
                gz-form-input
                .min=${MIN_SIZE}
                .max=${MAX_SIZE}
                .value=${DEFAULT_DEPTH}
              ></gz-input-number>
            </gz-form-field>
          </div>

          <gz-form-field id="rule-settings-scan-pua-field">
            <gz-checkbox
              label="${translateFn('Scan for potentially unwanted applications (PUA)')}"
              gz-form-input
              checked
            ></gz-checkbox>
          </gz-form-field>
        </div>

        <!-- Rule actions section -->
        <div id="rules-actions-section">
          <gz-title fontWeight="500" fontSize="18" class="module-subtitle mt-5">${translateFn('Actions')}</gz-title>

          ${this.renderMultipleDropdownsForActionSection(
            ['rule-actions-infected-action-field', 'rule-actions-infected-then-field'],
            translateFn('Default action for infected files'),
            [ExchangeAntimalwareRuleActionType.REMEDIATE, ExchangeAntimalwareRuleActionType.MOVE_TO_QUARANTINE]
          )}
          ${this.renderMultipleDropdownsForActionSection(
            ['rule-actions-suspected-action-field', 'rule-actions-suspected-then-field'],
            translateFn('Default action for suspicious files'),
            [ExchangeAntimalwareRuleActionType.REPORT_ONLY, ExchangeAntimalwareRuleActionType.REPORT_ONLY]
          )}
          ${this.renderMultipleDropdownsForActionSection(
            ['rule-actions-unscannable-action-field', 'rule-actions-unscannable-then-field'],
            translateFn('Default action for unscannable files'),
            [ExchangeAntimalwareRuleActionType.REPORT_ONLY, ExchangeAntimalwareRuleActionType.REPORT_ONLY]
          )}
        </div>
      </div>
    `;
  }

  /**
   * Renders multiple dropdowns for the action section
   * @param ids
   * @param label
   * @param values
   * @private
   */
  private renderMultipleDropdownsForActionSection(
    ids: string[],
    label: string,
    values: ExchangeAntimalwareRuleActionType[]
  ): TemplateResult {
    return html` <div class="multiple-dropdowns">
      <gz-form-field class="first-dropdown-field" id="${ids[0]}" label="${label}">
        <gz-dropdown
          gz-form-input
          .options=${getExchangeAntimalwareActionRuleFormOptions()}
          .value=${values[0]}
        ></gz-dropdown>
      </gz-form-field>

      <gz-form-field class="second-dropdown-field" id="${ids[1]}" label="${translateFn('Alternative action')}">
        <gz-dropdown
          gz-form-input
          .options=${getExchangeAntimalwareAlternativeActionRuleFormOptions()}
          .value=${values[1]}
        ></gz-dropdown>
      </gz-form-field>
    </div>`;
  }

  /**
   * On scan mode field change, show/hide extensions field.
   * @param event
   * @private
   */
  private handleSettingsScanModeFieldChange(event: CustomEvent) {
    const scanModeTypesWithExtensions = [
      ExchangeAntimalwareRuleSettingsScanMode.USER_DEFINED_EXTENSIONS,
      ExchangeAntimalwareRuleSettingsScanMode.ALL_FILES_EXCEPT_SPECIFIC_EXTENSIONS,
    ];
    const isExtensionsFieldVisible = scanModeTypesWithExtensions.includes(event.detail.value);

    // show/hide extensions field & validate it
    toggleVisibility(this.ruleSettingsExtensionsField, isExtensionsFieldVisible);
    this.ruleSettingsExtensionsField.disabled = !isExtensionsFieldVisible;
    this.ruleSettingsExtensionsField.validate(true);
  }

  /**
   * On radio selection change, disable unselected radio option.
   * @param event
   */
  private onSchedulerRecurrenceTypeChanged(event: CustomEvent) {
    const isScheduleType = event.detail.selectedValue === RecurrenceType.SCHEDULE;
    this.disableRecurrenceFields(!isScheduleType, isScheduleType);
  }

  /**
   * Enable/disable form fields based on received parameters.
   * @param isScheduleDisabled
   * @param isWeekdaysDisabled
   */
  private disableRecurrenceFields(isScheduleDisabled: boolean, isWeekdaysDisabled: boolean) {
    // disable/enable recurrence fields - run once every
    toggleDisabled(this.recurrenceStepFormField, isScheduleDisabled);
    toggleDisabled(this.recurrenceStepTypeField, isScheduleDisabled);

    // disable/enable recurrence fields & add validation - weekdays
    toggleDisabled(this.recurrenceRunEveryField, isWeekdaysDisabled);
    this.recurrenceRunEveryField.Validators = isWeekdaysDisabled
      ? []
      : [RequiredValidator(translateFn('Select at least one day of the week.'))];
  }

  /**
   * Validate target scan object fields. At least one must be selected
   * @private
   */
  private onTargetScanObjectChanged() {
    this.targetScanEmailsField?.validate(true);
    this.targetScanContactsField?.validate(true);
    this.targetScanTasksField?.validate(true);
    this.targetScanAppointmentsField?.validate(true);
    this.targetScanPostItemsField?.validate(true);
  }

  /**
   * Validate target exclusions object fields. At least one must be added.
   * @param event
   * @private
   */
  private onExclusionsEnabledChanged(event: CustomEvent) {
    toggleVisibility(this.targetExclusionsField, event.detail.checked);

    this.targetExclusionsField.Validators = event.detail.checked
      ? [RequiredValidator(translateFn('Required field'))]
      : [];
  }
}
